import type {
  Duration,
  RadioDurationFilterValueType,
} from '../../../components/StatelessComponent/SingleFilter/GenericBloc/GenericRadioDurationFilter/GenericRadioDurationType';
import {
  DURATION_UNITS,
  RadioDurationFilterOperation,
} from '../../../components/StatelessComponent/SingleFilter/GenericBloc/GenericRadioDurationFilter/GenericRadioDurationType';
import type { IntermediateFormat } from '../../../domains/global-filters/model/IntermediateFormat';
import type { QueryFormatFilter } from '../../../domains/global-filters/model/QueryFormat';
import {
  QueryMapOperandEnum,
  QueryKeyEnum,
} from '../../../domains/global-filters/model/QueryFormat';

const shouldNotHappen = (never: never): void => {
  throw new Error(`Unknown type: ${never}`);
};

function convertToMs(duration: Duration): number {
  switch (duration.unit) {
    case DURATION_UNITS.HOURS: {
      return duration.amount * 3600 * 1000;
    }
    case DURATION_UNITS.MINUTES: {
      return duration.amount * 60 * 1000;
    }
    case DURATION_UNITS.SECONDS: {
      return duration.amount * 1000;
    }
    default: {
      return 0;
    }
  }
}

export function fromUiToQueryFormat(uiValue?: RadioDurationFilterValueType): QueryFormatFilter {
  if (!uiValue) {
    return {};
  }
  switch (uiValue.operation) {
    case RadioDurationFilterOperation.IS_GREATER_THAN: {
      return {
        [QueryMapOperandEnum.GREATER_THAN]: {
          [QueryKeyEnum.duration]: convertToMs(uiValue.value),
        },
      };
    }
    case RadioDurationFilterOperation.IS_LESS_THAN: {
      return {
        [QueryMapOperandEnum.LESS_THAN]: { [QueryKeyEnum.duration]: convertToMs(uiValue.value) },
      };
    }
    case RadioDurationFilterOperation.IS: {
      return {
        [QueryMapOperandEnum.EQUAL]: { [QueryKeyEnum.duration]: convertToMs(uiValue.value) },
      };
    }
    case RadioDurationFilterOperation.IS_NOT: {
      // We ignore this
      break;
    }
    case RadioDurationFilterOperation.IS_BETWEEN: {
      return {
        [QueryMapOperandEnum.RANGE_IN]: {
          [QueryKeyEnum.duration]: [convertToMs(uiValue.from), convertToMs(uiValue.to)],
        },
      };
    }
    case RadioDurationFilterOperation.IS_UNKNOWN: {
      // We ignore this
      break;
    }
    case RadioDurationFilterOperation.HAS_ANY_VALUE: {
      // We ignore this
      break;
    }
    default: {
      shouldNotHappen(uiValue);
    }
  }

  return {};
}

function convertToAmountByUnit(durationInMs: number): Duration {
  const durationInSec = durationInMs / 1000;
  if (durationInSec !== 0 && durationInSec % 3600 === 0) {
    return {
      amount: durationInSec / 3600,
      unit: DURATION_UNITS.HOURS,
    };
  }
  if (durationInSec !== 0 && durationInSec % 60 === 0) {
    return {
      amount: durationInSec / 60,
      unit: DURATION_UNITS.MINUTES,
    };
  }

  return {
    amount: durationInSec,
    unit: DURATION_UNITS.SECONDS,
  };
}

export function fromIntermediateToUiFormat(
  intermediateValue: IntermediateFormat,
): RadioDurationFilterValueType {
  switch (intermediateValue.operation[0]) {
    case QueryMapOperandEnum.CONTAINS:
    case QueryMapOperandEnum.STARTS_WITH:
    case QueryMapOperandEnum.ENDS_WITH:
    case QueryMapOperandEnum.DAYS_AGO:
    case QueryMapOperandEnum.NOT:
    case QueryMapOperandEnum.REGEX:
    case QueryMapOperandEnum.SIMPLE_MATCH:
    case QueryMapOperandEnum.DROPOFF:
    case QueryMapOperandEnum.UNKNOWN: {
      // We ignore
      break;
    }
    case QueryMapOperandEnum.EQUAL: {
      return {
        operation: RadioDurationFilterOperation.IS,
        value: convertToAmountByUnit(+intermediateValue.value[0]),
      };
    }
    case QueryMapOperandEnum.GREATER_THAN: {
      return {
        operation: RadioDurationFilterOperation.IS_GREATER_THAN,
        value: convertToAmountByUnit(+intermediateValue.value[0]),
      };
    }
    case QueryMapOperandEnum.LESS_THAN: {
      return {
        operation: RadioDurationFilterOperation.IS_LESS_THAN,
        value: convertToAmountByUnit(+intermediateValue.value[0]),
      };
    }
    case QueryMapOperandEnum.RANGE_IN: {
      return {
        operation: RadioDurationFilterOperation.IS_BETWEEN,
        from: convertToAmountByUnit(+(intermediateValue.value as number[])[0]),
        to: convertToAmountByUnit(+(intermediateValue.value as number[])[1]),
      };
    }
    default: {
      shouldNotHappen(intermediateValue.operation[0]);
    }
  }
  throw new Error(`Operation not implemented: ${intermediateValue.operation[0]}`);
}
